import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AlertInfo, AlertService, AlertType } from '../Alert/alert.service';
import { BaseService } from '../base.service';

@Injectable({
  providedIn: 'root',
})
export class UserService extends BaseService {
  constructor(
    private http: HttpClient,
    alertService: AlertService
  ) {
    super('user', alertService);
  }

  getMe(): Observable<any> {
    const url = this.url + '/me';
    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => {
        // Let auth guard handle navigation and error display
        throw err;
      })
    );
  }

  searchAd(filter: string): Observable<any> {
    const url = this.url + '/search-ad';

    var params = new HttpParams();
    params = params.set('filter', filter);

    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Searching For AAD Users', err))
    );
  }

  findAd(filter: string): Observable<any> {
    const url = this.url + '/find-ad';

    var params = new HttpParams();
    params = params.set('filter', filter);

    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Find Users in AAD', err))
    );
  }

  search(filter: string): Observable<any> {
    const url = this.url + '/search';

    var params = new HttpParams();
    params = params.set('filter', filter);

    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Searching For EWRS Users', err))
    );
  }

  getDelegates(): Observable<any> {
    const url = this.url + '/delegates';

    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Users Delegates', err))
    );
  }

  getDelegations(filter: string): Observable<any> {
    const url = this.url + '/delegations';

    var params = new HttpParams().set('filter', filter.trim());

    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Users Delegations', err))
    );
  }

  hasDelegations(): Observable<any> {
    const url = this.url + '/hasDelegations';

    return this.http.get(url).pipe(
      map((data) => data),
      // No global alert: "Booking for" is hidden; fail closed to self-booking only.
      catchError(() => of({ value: false }))
    );
  }

  getLastBookingSelection(): Observable<any> {
    const url = this.url + '/last-booking-selection';
    return this.http.get(url).pipe(
      map((data) => data),
      // Non-critical: if this fails the form simply renders without pre-fill.
      catchError(() => of({ value: { hasSelection: false } }))
    );
  }

  createFromFile(body: any): Observable<any> {
    return this.http.post(`${this.url}/import`, body).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Adding User(s)', err))
    );
  }

  uploadBatch(file: any): Observable<any> {
    const url = this.url + '/batch';

    const body = JSON.stringify(file);
    const headers = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.http.post(url, body, headers).pipe(
      map((data) => data),
      catchError((err) => this.handleError('CSV file could not be imported.', err))
    );
  }

  updateUser(data: any, cancelReservationsOnDivisionChange: boolean = true): Observable<any> {
    const url = this.url;

    // always send the query param, defaults to true unless caller overrides
    let param = new HttpParams().set(
      'cancelReservationsOnDivisionChange',
      cancelReservationsOnDivisionChange.toString()
    );

    return this.http.patch(url, data, { params: param }).pipe(
      map((data) => data),
      catchError((err) => this.handleError("Error updating user's profile", err))
    );
  }

  getRoles(id: number, pageNumber = null, pageSize = null): Observable<any> {
    const url = this.url + '/roles/' + id;

    var params = new HttpParams();
    if (pageNumber != null && pageNumber >= 0)
      params = params.set('pageNumber', pageNumber.toString());

    if (pageSize != null && pageSize >= 0) params = params.set('pageSize', pageSize.toString());

    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting User Roles', err))
    );
  }

  getRolesTypes(): Observable<any> {
    const url = this.url + '/role-types';

    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting Roles', err))
    );
  }

  getUsers(
    pageNumber = null,
    pageSize = null,
    userFilter = null,
    roleId = null,
    sortOrder = 'asc',
    sortColumn = ''
  ): Observable<any> {
    const url = this.url;

    var params = new HttpParams();
    if (pageNumber >= 0) params = params.set('pageNumber', pageNumber.toString());

    if (pageSize >= 0) params = params.set('pageSize', pageSize.toString());

    if (userFilter) params = params.set('userFilter', userFilter.toString());

    if (roleId) params = params.set('roleId', roleId.toString());

    if (sortOrder) params = params.set('sortOrder', sortOrder.toString());

    if (sortColumn) params = params.set('sortColumn', sortColumn.toString());

    return this.http.get(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting User Roles', err))
    );
  }

  addUser(user: any): Observable<any> {
    const url = this.url;

    return this.http.post(url, user).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Adding User', err))
    );
  }

  getUser(id: number): Observable<any> {
    const url = this.url + '/' + id;

    return this.http.get(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Getting User', err))
    );
  }

  addRole(userRole: any): Observable<any> {
    const url = this.url + '/role';

    return this.http.post(url, userRole).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Assigning Role', err))
    );
  }

  removeRole(userId: number, roleId: number): Observable<any> {
    const url = this.url + '/role';

    var params = new HttpParams();
    params = params.set('userId', userId.toString());
    params = params.set('roleId', roleId.toString());

    return this.http.delete(url, { params: params }).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Removing Role Assignment', err))
    );
  }

  remove(id: number): Observable<any> {
    const url = this.url + '/' + id;
    return this.http.delete(url).pipe(
      map((data) => data),
      catchError((err) => this.handleError('Error Removing User', err))
    );
  }
}
